import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { EmptyState, StatusBadge, ColorSwatch, TableRowActions, SortableTh, TableFilterBar, TablePagination } from '@/shared/components'
import { useMatieres, useDeleteMatiere } from '../hooks/use-matieres'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useTableSort } from '@/shared/hooks/use-table-sort'
import { usePagination } from '@/shared/hooks/use-pagination'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'

export function MatiereList() {
  const { t } = useTranslation()
  const { data: matieres, isLoading } = useMatieres()
  const deleteMatiere = useDeleteMatiere()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [statusFilter, setStatusFilter] = useState('all')
  const [langueFilter, setLangueFilter] = useState('all')

  const filtered = useMemo(() => {
    if (!matieres) return []
    let result = matieres
    if (statusFilter === 'actif') result = result.filter((m) => m.actif)
    if (statusFilter === 'inactif') result = result.filter((m) => !m.actif)
    if (langueFilter === 'langue') result = result.filter((m) => m.estLangue)
    if (langueFilter === 'autre') result = result.filter((m) => !m.estLangue)
    return result
  }, [matieres, statusFilter, langueFilter])

  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(filtered, 'nom')
  const { paginatedData, currentPage, totalPages, totalItems, pageSize, setCurrentPage } = usePagination(sortedData)

  const [deleteDialog, setDeleteDialog] = useState<{ id: number; label: string } | null>(null)

  const handleDelete = (id: number, nom: string) => {
    setDeleteDialog({ id, label: nom })
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteMatiere.mutate(deleteDialog.id)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!matieres || matieres.length === 0) {
    return <EmptyState message={t('administration.sections.subjects.noSubjects', 'Aucune matière disponible. Créez votre première matière !')} />
  }

  const counts = {
    all: matieres.length,
    actif: matieres.filter((m) => m.actif).length,
    inactif: matieres.filter((m) => !m.actif).length,
  }

  const langueCount = matieres.filter((m) => m.estLangue).length
  const autreCount = matieres.filter((m) => !m.estLangue).length

  return (
    <>
      <TableFilterBar
        filters={[
          {
            key: 'status',
            label: 'Statut',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous', count: counts.all },
              { value: 'actif', label: 'Actifs', count: counts.actif },
              { value: 'inactif', label: 'Inactifs', count: counts.inactif },
            ],
          },
          {
            key: 'langue',
            label: 'Type',
            value: langueFilter,
            options: [
              { value: 'all', label: 'Tous', count: matieres.length },
              { value: 'langue', label: 'Langues', count: langueCount },
              { value: 'autre', label: 'Autres', count: autreCount },
            ],
          },
        ]}
        onFilterChange={(key, value) => {
          if (key === 'status') setStatusFilter(value)
          if (key === 'langue') setLangueFilter(value)
        }}
      />
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
              <SortableTh label="Nom" sortKey="nom" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Code" sortKey="codeMatiere" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Couleur" sortKey="couleur" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Langue" sortKey="estLangue" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Statut" sortKey="actif" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {paginatedData.map((matiere) => (
              <tr key={matiere.id} className={cn('transition-colors hover:bg-neutral-50', !matiere.actif && 'opacity-50')}>
                <td className="px-4 py-3 text-sm font-medium text-neutral-900">{matiere.nom}</td>
                <td className="px-4 py-3"><StatusBadge label={matiere.codeMatiere} variant="info" /></td>
                <td className="px-4 py-3"><ColorSwatch color={matiere.couleur} /></td>
                <td className="px-4 py-3 text-center">
                  {matiere.estLangue && (
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {matiere.actif ? <StatusBadge label="Actif" variant="active" /> : <StatusBadge label="Inactif" variant="inactive" />}
                    {matiere.estSysteme && <StatusBadge label="Système" variant="system" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  {(!matiere.estSysteme || isAdmin) && (
                    <TableRowActions
                      editTo={`/administration/matieres/${matiere.id}`}
                      onDelete={() => handleDelete(matiere.id, matiere.nom)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={`Supprimer la matière "${deleteDialog?.label}" ?`}
        description="Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </>
  )
}
